<template>
    <Teleport to="body">
        <Transition name="dialog">
            <div v-if="open" class="dialog-overlay" @mousedown.self="cancel">
                <div
                    ref="dialogRef"
                    class="dialog"
                    role="alertdialog"
                    aria-modal="true"
                    :aria-labelledby="titleId"
                    tabindex="-1"
                    @keydown.esc.stop="cancel"
                >
                    <h2 :id="titleId" class="dialog__title">{{ title }}</h2>
                    <div class="dialog__body">
                        <slot />
                    </div>
                    <div class="dialog__actions">
                        <button type="button" class="dialog__button dialog__button--secondary" :disabled="busy" @click="cancel">
                            {{ cancelLabel }}
                        </button>
                        <button
                            type="button"
                            :class="['dialog__button', `dialog__button--${tone}`]"
                            :disabled="busy"
                            @click="emit('confirm')"
                        >
                            {{ busy ? 'Подождите…' : confirmLabel }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue';

const props = defineProps({
    open: { type: Boolean, required: true },
    title: { type: String, required: true },
    confirmLabel: { type: String, default: 'Подтвердить' },
    cancelLabel: { type: String, default: 'Отмена' },
    tone: { type: String, default: 'primary', validator: value => ['primary', 'danger'].includes(value) },
    busy: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'cancel']);

const dialogRef = ref(null);
const titleId = `dialog-title-${Math.random().toString(36).slice(2, 9)}`;

const cancel = () => {
    if (!props.busy) emit('cancel');
};

watch(() => props.open, async (isOpen) => {
    if (!isOpen) return;
    await nextTick();
    dialogRef.value?.focus();
});
</script>

<style scoped lang="scss">
@use '../../../scss/pages/admin/admin-tokens' as *;

.dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: $z-dialog;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $spacing-lg;
    background: rgba(17, 24, 39, 0.45);
}

.dialog {
    width: min(460px, 100%);
    padding: $spacing-xl;
    border-radius: $radius-xl;
    background: $admin-bg-primary;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    outline: none;
}

.dialog__title {
    margin: 0 0 $spacing-md;
    font-size: $font-size-xl;
    font-weight: 600;
    color: $admin-text-primary;
}

.dialog__body {
    font-size: $font-size-base;
    color: $admin-text-secondary;
    line-height: 1.55;

    :deep(p) { margin: 0 0 $spacing-sm; }
    :deep(ul) { margin: 0 0 $spacing-sm; padding-left: 1.25rem; }
}

.dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: $spacing-md;
    margin-top: $spacing-xl;
}

.dialog__button {
    padding: $spacing-sm $spacing-lg;
    border: none;
    border-radius: $radius-md;
    font-size: $font-size-base;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &--secondary {
        background: $admin-border-color;
        color: $admin-text-secondary;

        &:hover:not(:disabled) { background: $admin-border-hover; }
    }

    &--primary {
        background: $admin-primary-color;
        color: #fff;

        &:hover:not(:disabled) { background: $admin-primary-hover; }
    }

    &--danger {
        background: $admin-danger-color;
        color: #fff;

        &:hover:not(:disabled) { background: #b91c1c; }
    }
}

.dialog-enter-active,
.dialog-leave-active {
    transition: opacity 0.15s ease;
}

.dialog-enter-from,
.dialog-leave-to {
    opacity: 0;
}
</style>
